import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AutoPricingCompListingRowOffset } from 'src/components/AutoPricing/AutoPricingCompListingRowOffset/AutoPricingCompListingRowOffset';
import { AutoPricingCompQuantitiesSelectorV2 } from 'src/components/AutoPricing/AutoPricingCompQuantitiesSelector/AutoPricingCompQuantitiesSelectorV2';
import { useAutoPricingSettingsSelectedSections } from 'src/components/AutoPricing/AutoPricingSelectedSectionSettingsInput/AutoPricingSelectedSectionSettingsInput.hooks';
import { SectionFilterRowNudger } from 'src/components/AutoPricing/components/SectionFilterRowNudger';
import { FilterPillList } from 'src/components/common/FilterPill';
import { Content } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useInventoryEventDetailContext } from 'src/contexts/InventoryEventDetailContext/InventoryEventDetailContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ExpandIcon, FoldIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { AutoPricingInputs, Feature } from 'src/WebApiController';

import { CompAdvancedPricingSettingsContainer } from './CompAdvancedPricingSettingsContainer';
import * as styles from './CompPricingSettings.css';
import { CompSelectedSectionRecommendations } from './CompSelectedSectionsRecommendations';

const quantitiesOptions = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10+',
].reduce(
  (r, c) => {
    r[c] = c;
    return r;
  },
  {} as Record<string, string>
);

export const CompPricingSettings = ({
  useVerticalView,
}: {
  useVerticalView?: boolean;
}) => {
  const { venueMapInfo } = useEventMapContext();
  const { selectedListing, selectedGroup, recommendingSectionIds } =
    useInventoryEventDetailContext();

  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);

  const { setValue, watch } = useFormContext<AutoPricingInputs>();

  const {
    compListingExcludeFanInventory,
    compListingExcludeDefects,
    compListingIncludeUnmappedInventory,
    compListingQuantityScoreAdjustmentOverrideJson,
    compListingQuantityFilters,
    compListingSelectedSectionSettings,
  } = watch();

  const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(
    false ||
      !!compListingExcludeFanInventory ||
      !!compListingIncludeUnmappedInventory
  );

  useEffect(() => {
    setShowAdvancedSettings((prev) => {
      if (prev) {
        return prev;
      }

      return (
        !!compListingExcludeFanInventory ||
        !!compListingIncludeUnmappedInventory
      );
    });
  }, [compListingExcludeFanInventory, compListingIncludeUnmappedInventory]);

  const { onRowIdFiltersChange, onSectionIdFiltersChange, onRowOffsetChange } =
    useAutoPricingSettingsSelectedSections(venueMapInfo?.sections);

  const selectedSections = useMemo(() => {
    const formSectionFilterIds =
      compListingSelectedSectionSettings?.sectionIdFilter ?? [];
    return (
      venueMapInfo?.sections?.filter((section) => {
        return formSectionFilterIds.includes(section.id);
      }) ?? []
    );
  }, [compListingSelectedSectionSettings?.sectionIdFilter, venueMapInfo]);

  const rowIdFilters = useMemo(() => {
    return compListingSelectedSectionSettings?.rowIdFilter ?? [];
  }, [compListingSelectedSectionSettings]);

  const sectionRowIdFilters = useMemo(() => {
    return compListingSelectedSectionSettings?.sectionRowIdFilter ?? {};
  }, [compListingSelectedSectionSettings]);

  const toggleShowAdvancedSettings = () => {
    setShowAdvancedSettings((prev) => !prev);
  };

  const sectionOnlyFilterPillList = useMemo(() => {
    return compListingSelectedSectionSettings?.rowOffset != null;
  }, [compListingSelectedSectionSettings?.rowOffset]);

  if (!hasAutoPricingFeature) {
    return null;
  }

  return (
    <div className={styles.compSettingsContainer}>
      <CompSelectedSectionRecommendations />
      <Stack width="full" gap="m" alignItems="center">
        <FilterPillList
          selectedSections={selectedSections}
          rowIdFilters={rowIdFilters}
          sectionRowIdFilters={sectionRowIdFilters}
          animatedSectionIds={recommendingSectionIds}
          scrollButtonClassName={styles.pillItemsScrollButton}
          applyRowFilterChanges={onRowIdFiltersChange}
          applySelectedSectionChanges={onSectionIdFiltersChange}
          useCondensedBorders={true}
          sectionOnly={sectionOnlyFilterPillList}
          allowRowRange
        />
        {!!Object.entries(sectionRowIdFilters).length && (
          <SectionFilterRowNudger
            selectedSections={selectedSections}
            rowIdFilters={rowIdFilters}
            sectionRowIdFilters={sectionRowIdFilters}
            applyRowFilterChanges={onRowIdFiltersChange}
            isCondensedView={true}
          />
        )}
      </Stack>

      <Stack gap="s" alignItems="center" className={styles.qtyFilterWrapper}>
        <Stack gap="s" alignItems="center" width="full">
          <AutoPricingCompListingRowOffset
            listingId={selectedListing?.id ?? selectedGroup?.id}
            rowOffset={compListingSelectedSectionSettings?.rowOffset}
            onRowOffsetChange={onRowOffsetChange}
          />
          <AutoPricingCompQuantitiesSelectorV2
            listing={selectedListing}
            leftIcon={
              <div className={styles.qtyText}>
                <Content id={ContentId.Quantity} />:
              </div>
            }
            quantityFilters={compListingQuantityFilters}
            quantityScoreAdjustmentOverrideJson={
              compListingQuantityScoreAdjustmentOverrideJson
            }
            onUpdateQuantityFilters={(
              quantityFilters,
              quantityFilterScoreAdjustmentOverrideJson
            ) => {
              setValue('compListingQuantityFilters', quantityFilters);
              setValue(
                'compListingQuantityScoreAdjustmentOverrideJson',
                quantityFilterScoreAdjustmentOverrideJson
              );
            }}
          />
        </Stack>

        {useVerticalView && (
          <CompAdvancedPricingSettingsContainer
            useVerticalView={useVerticalView}
          />
        )}
      </Stack>

      {!useVerticalView && (
        <>
          <Stack
            justifyContent="spaceBetween"
            alignItems="center"
            className={clsx(styles.advancedSettingsContainer, {
              [styles.advancedSettingsContainerHide]: !showAdvancedSettings,
            })}
            onClick={toggleShowAdvancedSettings}
          >
            {showAdvancedSettings ? (
              <>
                <Content id={ContentId.HideAdvancedSettings} />
                <FoldIcon size={vars.iconSize.m} />
              </>
            ) : (
              <>
                <Content id={ContentId.ShowAdvancedSettings} />
                <ExpandIcon size={vars.iconSize.m} />
              </>
            )}
          </Stack>

          {showAdvancedSettings && (
            <CompAdvancedPricingSettingsContainer
              useVerticalView={useVerticalView}
            />
          )}
        </>
      )}
    </div>
  );
};
